const TOOL_OUTPUT_DISPLAY_MAX_CHARS = 8_000;
const CHIP_MAX_CHARS = 56;
const TRUNCATION_NOTE = /\n\n… truncated [\d,]+ characters for display$/;
const GENERIC_STATUS = new Set([
  'ok',
  'success',
  'completed',
  'complete',
  'done',
]);
const COMPARE_LABELS: Record<string, string> = {
  previous_period: 'vs previous',
  previous: 'vs previous',
  previous_year: 'vs last year',
  year_over_year: 'vs last year',
  yoy: 'vs last year',
  same_period_last_year: 'vs last year',
};

const CHIP_KEYS = [
  'query',
  'q',
  'search',
  'title',
  'name',
  'path',
  'file',
  'filename',
  'summary',
  'subject',
  'content',
  'status',
  'model',
  'jobNumber',
  'job_number',
  'jobId',
  'job_id',
  'id',
] as const;

const COLLECTION_KEYS = [
  'jobs',
  'hits',
  'results',
  'items',
  'records',
  'documents',
  'rows',
] as const;

const WRAPPER_KEYS = ['result', 'data', 'output', 'payload'] as const;

const AGGREGATE_ENVELOPE_KEYS = [
  'runId',
  'status',
  'generatedAt',
  'companyId',
  'query',
  'period',
  'periods',
  'summary',
  'totals',
  'notes',
] as const;

const ROW_PREVIEW_LIMIT = 5;

/** FSI Aggregate Tools. Their body is not the jobs `query.metric` envelope. */
const AGGREGATE_TOOL_NAMES = new Set([
  'invoice_summary',
  'quote_summary',
  'position_snapshot',
]);

/** Filter and coverage fields stay whole. The customer chip counts every contact id. */
const AGGREGATE_TOOL_PRESERVED_KEYS = new Set([
  'filters',
  'coverage',
  'period',
  'fetchWindow',
]);

const skipJsonWhitespace = (source: string, index: number) => {
  while (index < source.length && /\s/.test(source[index])) index += 1;
  return index;
};

const takeJsonString = (
  source: string,
  index: number
): { value: string; end: number } | null => {
  if (source[index] !== '"') return null;

  let cursor = index + 1;
  let escaped = false;
  for (; cursor < source.length; cursor += 1) {
    if (escaped) {
      escaped = false;
      continue;
    }
    if (source[cursor] === '\\') {
      escaped = true;
      continue;
    }
    if (source[cursor] === '"') {
      try {
        return {
          value: JSON.parse(source.slice(index, cursor + 1)) as string,
          end: cursor + 1,
        };
      } catch {
        return null;
      }
    }
  }

  return null;
};

const takeBalancedJson = (
  source: string,
  index: number
): { raw: string; end: number } | null => {
  const opener = source[index];
  if (opener !== '{' && opener !== '[') return null;

  const stack = [opener === '{' ? '}' : ']'];
  let inString = false;
  let escaped = false;

  for (let cursor = index + 1; cursor < source.length; cursor += 1) {
    const char = source[cursor];
    if (inString) {
      if (escaped) {
        escaped = false;
        continue;
      }
      if (char === '\\') {
        escaped = true;
        continue;
      }
      if (char === '"') inString = false;
      continue;
    }
    if (char === '"') {
      inString = true;
      continue;
    }
    if (char === '{' || char === '[') {
      stack.push(char === '{' ? '}' : ']');
      continue;
    }
    if (char === '}' || char === ']') {
      if (stack.pop() !== char) return null;
      if (!stack.length) {
        return { raw: source.slice(index, cursor + 1), end: cursor + 1 };
      }
    }
  }

  return null;
};

const takeJsonValue = (
  source: string,
  index: number
): { value: unknown; end: number } | null => {
  const start = skipJsonWhitespace(source, index);
  if (start >= source.length) return null;

  const quoted = takeJsonString(source, start);
  if (quoted) return quoted;

  const balanced = takeBalancedJson(source, start);
  if (balanced) {
    try {
      return { value: JSON.parse(balanced.raw), end: balanced.end };
    } catch {
      return null;
    }
  }

  const literal = source
    .slice(start)
    .match(/^(true|false|null|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/);
  if (!literal) return null;

  try {
    return { value: JSON.parse(literal[0]), end: start + literal[0].length };
  } catch {
    return null;
  }
};

/** Recover complete top-level fields from pretty JSON cut mid-payload. */
const parsePartialJsonObject = (
  source: string
): Record<string, unknown> | null => {
  const trimmed = source.trim();
  if (!trimmed.startsWith('{')) return null;

  const record: Record<string, unknown> = {};
  let index = skipJsonWhitespace(trimmed, 1);

  while (index < trimmed.length) {
    if (trimmed[index] === '}') break;
    if (trimmed[index] === ',') {
      index = skipJsonWhitespace(trimmed, index + 1);
      continue;
    }

    const key = takeJsonString(trimmed, index);
    if (!key) break;

    index = skipJsonWhitespace(trimmed, key.end);
    if (trimmed[index] !== ':') break;

    const parsed = takeJsonValue(trimmed, index + 1);
    if (!parsed) break;

    record[key.value] = parsed.value;
    index = skipJsonWhitespace(trimmed, parsed.end);
  }

  return Object.keys(record).length ? record : null;
};

const compactAggregateOutput = (output: unknown): unknown => {
  const record = asRecord(output);
  if (!record) return output;

  const query = asRecord(record.query);
  const isAggregate = Boolean(query?.metric || record.period || record.periods);
  if (!isAggregate) return output;

  const rows = Array.isArray(record.rows) ? record.rows : null;
  const compacted: Record<string, unknown> = {};

  for (const key of AGGREGATE_ENVELOPE_KEYS) {
    if (key in record) compacted[key] = record[key];
  }

  if (rows) {
    compacted.rows = rows.slice(0, ROW_PREVIEW_LIMIT);
    if (rows.length > ROW_PREVIEW_LIMIT) {
      compacted.rowPreview = {
        shown: ROW_PREVIEW_LIMIT,
        total: rows.length,
      };
    }
  }

  return compacted;
};

const isAggregateToolResult = (
  record: Record<string, unknown>,
  toolName?: string
) => {
  if (typeof record.error === 'string') return false;
  if (!asRecord(record.coverage)) return false;
  if (typeof toolName === 'string' && AGGREGATE_TOOL_NAMES.has(toolName)) {
    return true;
  }
  return typeof record.entity === 'string' && record.entity.trim() !== '';
};

/** Keep the Aggregate Tool body. Cut every long list to the row preview. */
const truncateAggregateToolValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(item => truncateAggregateToolValue(item));
  }

  const record = asRecord(value);
  if (!record) return value;

  const next: Record<string, unknown> = {};
  let previewTotal: number | null = null;

  for (const [key, child] of Object.entries(record)) {
    if (AGGREGATE_TOOL_PRESERVED_KEYS.has(key)) {
      next[key] = child;
      continue;
    }

    if (Array.isArray(child) && child.length > ROW_PREVIEW_LIMIT) {
      next[key] = child
        .slice(0, ROW_PREVIEW_LIMIT)
        .map(item => truncateAggregateToolValue(item));
      if (previewTotal === null || child.length > previewTotal) {
        previewTotal = child.length;
      }
      continue;
    }

    next[key] = truncateAggregateToolValue(child);
  }

  if (previewTotal !== null) {
    next.rowPreview = {
      shown: ROW_PREVIEW_LIMIT,
      total: previewTotal,
    };
  }

  return next;
};

const compactAggregateToolOutput = (
  output: unknown,
  toolName?: string
): unknown | null => {
  const record = asRecord(output);
  if (!record || !isAggregateToolResult(record, toolName)) return null;
  return truncateAggregateToolValue(record);
};

const compactJobListOutput = (output: unknown): unknown => {
  const record = asRecord(output);
  if (!record || !Array.isArray(record.jobs)) return output;
  if (!('totalJobs' in record || 'page' in record)) return output;

  const jobs = record.jobs;
  return {
    totalJobs: record.totalJobs,
    page: record.page,
    pageSize: record.pageSize,
    totalPages: record.totalPages,
    jobs: jobs.slice(0, ROW_PREVIEW_LIMIT),
    ...(jobs.length > ROW_PREVIEW_LIMIT
      ? {
          jobPreview: {
            shown: ROW_PREVIEW_LIMIT,
            total:
              typeof record.totalJobs === 'number'
                ? record.totalJobs
                : jobs.length,
          },
        }
      : {}),
  };
};

export function formatToolOutput(output: unknown): string {
  return typeof output === 'string' ? output : JSON.stringify(output, null, 2);
}

/** Cap tool JSON before Shiki/DOM so mobile chat does not retain multi‑MB highlighted trees. */
export function formatToolOutputForDisplay(
  output: unknown,
  toolName?: string
): string {
  const formatted = formatToolOutput(
    compactAggregateToolOutput(output, toolName) ??
      compactJobListOutput(compactAggregateOutput(output))
  );
  if (formatted.length <= TOOL_OUTPUT_DISPLAY_MAX_CHARS) {
    return formatted;
  }

  const omitted = formatted.length - TOOL_OUTPUT_DISPLAY_MAX_CHARS;
  return `${formatted.slice(0, TOOL_OUTPUT_DISPLAY_MAX_CHARS)}\n\n… truncated ${omitted.toLocaleString()} characters for display`;
}

/** Drop bulky tool payloads from chat state; UI only needs a short inspectable preview. */
export function compactToolCallOutput<T extends { output: unknown }>(
  toolOutput: T
): T {
  const toolName =
    'toolName' in toolOutput && typeof toolOutput.toolName === 'string'
      ? toolOutput.toolName
      : undefined;

  return {
    ...toolOutput,
    output: formatToolOutputForDisplay(toolOutput.output, toolName),
  };
}

const truncateChip = (value: string) => {
  const trimmed = value.replace(/\s+/g, ' ').trim();
  if (trimmed.length <= CHIP_MAX_CHARS) return trimmed;
  return `${trimmed.slice(0, CHIP_MAX_CHARS - 1)}…`;
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const parseToolOutput = (output: unknown): unknown => {
  if (typeof output !== 'string') return output;

  const trimmed = output.replace(TRUNCATION_NOTE, '').trim();
  if (!trimmed) return '';

  try {
    return JSON.parse(trimmed);
  } catch {
    const partial = parsePartialJsonObject(trimmed);
    if (partial) return partial;
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) return '';
    return trimmed;
  }
};

const usageChipParts = (record: Record<string, unknown>): string[] => {
  const input =
    record.input_tokens ?? record.prompt_tokens ?? record.inputTokens;
  const output =
    record.output_tokens ?? record.completion_tokens ?? record.outputTokens;
  const parts: string[] = [];

  if (typeof input === 'number') parts.push(`${input.toLocaleString()} in`);
  if (typeof output === 'number') parts.push(`${output.toLocaleString()} out`);

  return parts;
};

const collectionChip = (
  record: Record<string, unknown>,
  allowEmpty: boolean
): string | null => {
  const jobCount = record.totalJobs ?? record.total_jobs;
  if (typeof jobCount === 'number' && (allowEmpty || jobCount > 0)) {
    return `${jobCount.toLocaleString()} job${jobCount === 1 ? '' : 's'}`;
  }

  for (const key of COLLECTION_KEYS) {
    const value = record[key];
    if (!Array.isArray(value)) continue;
    if (!allowEmpty && value.length === 0) continue;

    const label = key === 'jobs' ? 'job' : key.replace(/s$/, '');
    return `${value.length} ${label}${value.length === 1 ? '' : 's'}`;
  }

  const count = record.count ?? record.total ?? record.length;
  if (typeof count === 'number' && (allowEmpty || count > 0)) {
    return `${count.toLocaleString()} result${count === 1 ? '' : 's'}`;
  }

  return null;
};

const humanizeToken = (value: string) =>
  value.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();

/** Words from camelCase or snake_case. A job worksheet result reads "job worksheets". */
const humanizeEntityType = (value: string) => {
  const label = value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  return label === 'job' ? 'job worksheets' : label;
};

const entityTypeChip = (record: Record<string, unknown>): string | null => {
  const raw = record.entityType ?? record.entity_type;
  if (typeof raw !== 'string') return null;
  const label = humanizeEntityType(raw);
  return label || null;
};

const periodLabelFrom = (value: unknown): string => {
  if (typeof value === 'string' && value.trim()) {
    return /[_-]/.test(value) ? humanizeToken(value) : value.trim();
  }

  const record = asRecord(value);
  return typeof record?.label === 'string' && record.label.trim()
    ? record.label.trim()
    : '';
};

const compareChip = (
  compareTo: unknown,
  hasPreviousPeriod: boolean
): string | null => {
  if (typeof compareTo === 'string' && compareTo.trim()) {
    const key = compareTo.trim().toLowerCase();
    if (key === 'none' || key === 'null' || key === 'false') return null;
    return COMPARE_LABELS[key] ?? `vs ${humanizeToken(compareTo)}`;
  }

  return hasPreviousPeriod ? 'vs previous' : null;
};

const aggregateChipParts = (record: Record<string, unknown>): string[] => {
  const query = asRecord(record.query);
  const periods = asRecord(record.periods);
  const current = periods ? asRecord(periods.current) : null;
  const previous = periods ? asRecord(periods.previous) : null;

  const periodLabel =
    periodLabelFrom(current) ||
    periodLabelFrom(record.period) ||
    periodLabelFrom(query?.period);
  const comparison = compareChip(query?.compareTo, Boolean(previous));
  const metric =
    typeof query?.metric === 'string' && query.metric.trim()
      ? humanizeToken(query.metric)
      : '';
  const groupBy =
    typeof query?.groupBy === 'string' && query.groupBy.trim()
      ? `by ${humanizeToken(query.groupBy)}`
      : '';

  if (!periodLabel && !comparison && !metric) return [];

  return [metric, periodLabel, groupBy, comparison].filter(
    (part): part is string => Boolean(part)
  );
};

const isoDayLabel = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!match) return '';
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return '';
  return `${match[3]}/${match[2]}/${match[1].slice(2)}`;
};

const aggregateToolPeriodChip = (period: unknown): string => {
  if (typeof period === 'string' && period.trim()) {
    return /[_-]/.test(period) ? humanizeToken(period) : period.trim();
  }

  const record = asRecord(period);
  if (!record) return '';

  const name = typeof record.name === 'string' ? record.name.trim() : '';
  if (name) return humanizeToken(name);

  const from = isoDayLabel(record.from);
  const to = isoDayLabel(record.to);
  if (from && to) return `${from}–${to}`;
  return from || to;
};

const aggregateToolCustomerChip = (filters: unknown): string => {
  const record = asRecord(filters);
  const account = asRecord(record?.account);
  const parentName =
    typeof account?.parentName === 'string' ? account.parentName.trim() : '';
  if (parentName) {
    const contactCount = account?.contactCount;
    if (typeof contactCount === 'number' && contactCount > 1) {
      return `${parentName} +${contactCount} contacts`;
    }
    return parentName;
  }

  const contactIds = record?.contactIds;
  if (Array.isArray(contactIds) && contactIds.length > 0) {
    const count = contactIds.length;
    return `${count.toLocaleString('en-GB')} contact${count === 1 ? '' : 's'}`;
  }

  return 'all customers';
};

const aggregateToolChipParts = (record: Record<string, unknown>): string[] => {
  const filters = asRecord(record.filters);
  const grouping = asRecord(record.grouping);
  const groupByRaw = filters?.groupBy ?? grouping?.groupBy;
  const groupBy = typeof groupByRaw === 'string' ? groupByRaw.trim() : '';
  const coverage = asRecord(record.coverage);
  const parts = [
    aggregateToolPeriodChip(record.period),
    aggregateToolCustomerChip(record.filters),
    groupBy ? `by ${humanizeToken(groupBy)}` : '',
  ];

  if (coverage?.complete === false) parts.push('partial');
  else if (coverage?.complete === true) parts.push('complete');
  if (coverage?.provisional === true) parts.push('provisional');
  if (
    typeof coverage?.rowsRead === 'number' &&
    Number.isFinite(coverage.rowsRead)
  ) {
    parts.push(`${coverage.rowsRead.toLocaleString('en-GB')} read`);
  }

  return parts.map(truncateChip).filter(part => part.trim());
};

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

type WallClock = {
  year: number;
  month: number;
  day: number;
  hours: number;
  minutes: number;
};

const wallClockFrom = (value: unknown): WallClock | null => {
  if (typeof value !== 'string') return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!match) return null;
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  return {
    year: Number(match[1]),
    month,
    day: Number(match[3]),
    hours: Number(match[4]),
    minutes: Number(match[5]),
  };
};

const clockText = (clock: WallClock) =>
  `${String(clock.hours).padStart(2, '0')}:${String(clock.minutes).padStart(2, '0')}`;

const sameDay = (start: WallClock, end: WallClock) =>
  start.year === end.year && start.month === end.month && start.day === end.day;

const eventSlotChip = (start: WallClock, end: WallClock | null) => {
  const day = `${start.day} ${MONTHS[start.month - 1]}`;
  if (!end || sameDay(start, end)) {
    return `${day}, ${clockText(start)}${end ? `–${clockText(end)}` : ''}`;
  }
  const endDay = `${end.day} ${MONTHS[end.month - 1]}`;
  return `${day} ${clockText(start)} – ${endDay} ${clockText(end)}`;
};

/** Title when the payload has one. Otherwise the clock time, never the Graph id. */
const calendarEventChip = (record: Record<string, unknown>): string | null => {
  const start = wallClockFrom(asRecord(record.start)?.dateTime);
  if (!start) return null;
  const looksLikeEvent =
    typeof record.subject === 'string' ||
    typeof record.summary === 'string' ||
    typeof record.iCalUId === 'string' ||
    typeof record.htmlLink === 'string' ||
    record.organizer != null;
  if (!looksLikeEvent) return null;

  const subject =
    typeof record.subject === 'string' ? record.subject.trim() : '';
  if (subject) return truncateChip(subject);
  const summary =
    typeof record.summary === 'string' ? record.summary.trim() : '';
  if (summary) return truncateChip(summary);

  return truncateChip(
    eventSlotChip(start, wallClockFrom(asRecord(record.end)?.dateTime))
  );
};

const textField = (value: unknown) =>
  typeof value === 'string' ? value.trim() : '';

/** Title, name, or subject. A deal status like "open" is not the label. */
const primaryLabel = (record: Record<string, unknown>): string =>
  textField(record.title) ||
  textField(record.name) ||
  textField(record.subject) ||
  textField(record.summary);

const outcomeChip = (record: Record<string, unknown>): string | null => {
  const comment = asRecord(record.comment);
  if (comment) {
    const caseId = comment.case_id ?? comment.caseId;
    if (caseId != null && String(caseId).trim()) {
      return `case ${caseId}`;
    }
  }

  if (primaryLabel(record)) return null;

  if (typeof record.status === 'string' && record.status.trim()) {
    const status = record.status.trim();
    if (!GENERIC_STATUS.has(status.toLowerCase())) {
      return truncateChip(status);
    }
  }

  return null;
};

const chipPartsFromValue = (
  value: unknown,
  depth = 0,
  toolName?: string
): string[] => {
  if (value == null) return [];
  if (typeof value === 'string' || typeof value === 'number') {
    const text = truncateChip(String(value));
    return text ? [text] : [];
  }
  if (typeof value === 'boolean') return [value ? 'true' : 'false'];
  if (Array.isArray(value)) {
    return [`${value.length} result${value.length === 1 ? '' : 's'}`];
  }

  const record = asRecord(value);
  if (!record || depth > 2) return [];

  if (record._truncated === true) return ['truncated'];

  if (isAggregateToolResult(record, depth === 0 ? toolName : undefined)) {
    return aggregateToolChipParts(record);
  }

  const usage = usageChipParts(record);
  if (usage.length) return usage;

  const outcome = outcomeChip(record);
  if (outcome) return [outcome];

  const aggregate = aggregateChipParts(record);
  if (aggregate.length) return aggregate;

  const collection = collectionChip(record, false);
  if (collection) return [collection];

  const eventChip = calendarEventChip(record);
  if (eventChip) return [eventChip];

  for (const key of CHIP_KEYS) {
    if (key === 'status') continue;
    const field = record[key];
    if (typeof field === 'string' && field.trim()) {
      return [truncateChip(field)];
    }
    if (typeof field === 'number') return [String(field)];
  }

  const entityType = entityTypeChip(record);
  if (entityType) return [truncateChip(entityType)];

  for (const key of WRAPPER_KEYS) {
    if (key in record) {
      const nested = chipPartsFromValue(record[key], depth + 1);
      if (nested.length) return nested;
    }
  }

  const emptyCollection = collectionChip(record, true);
  if (emptyCollection) return [emptyCollection];

  const keys = Object.keys(record).filter(key => {
    if (key.startsWith('_')) return false;
    if (key !== 'status') return true;
    return !GENERIC_STATUS.has(String(record.status).toLowerCase());
  });
  return keys.length ? [truncateChip(keys.slice(0, 3).join(', '))] : [];
};

/** Tool runners replace a huge payload with `{ preview }` plus truncation flags. */
const unwrapTruncatedPreview = (value: unknown): unknown => {
  const record = asRecord(value);
  if (
    !record ||
    record._truncated !== true ||
    typeof record.preview !== 'string'
  ) {
    return value;
  }

  const preview = record.preview.trim();
  if (!preview) return value;

  try {
    return JSON.parse(preview);
  } catch {
    return parsePartialJsonObject(preview) ?? value;
  }
};

/** A tool result as an object again, after chat state cut it to a display string. */
export const readToolPayload = (output: unknown): unknown =>
  unwrapTruncatedPreview(parseToolOutput(output));

/** Discrete pills for a completed tool row (metric, period, comparison, …). */
export function summarizeToolChipParts(
  output: unknown,
  toolName?: string
): string[] {
  const parsed = unwrapTruncatedPreview(parseToolOutput(output));
  const parts = chipPartsFromValue(parsed, 0, toolName).filter(part =>
    part.trim()
  );
  if (parts.length) return parts;

  const record = asRecord(parsed);
  if (typeof record?.status === 'string' && record.status.trim()) {
    return [record.status.trim()];
  }

  return ['ok'];
}

/** Short inline chip for a completed tool row (query, count, usage, …). */
export function summarizeToolChip(output: unknown, toolName?: string): string {
  return summarizeToolChipParts(output, toolName).join(' · ');
}

const GMAIL_MESSAGE_PREFIX = 'https://mail.google.com/mail/u/0/#all/';
const LINK_WALK_DEPTH = 5;

export type ExternalResourceLink = {
  href: string;
  service: 'calendar' | 'gmail' | 'outlook' | 'pipedrive';
  label: string;
};

const isCalendarEventUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:') return false;
    const host = url.hostname;
    return (
      (host === 'www.google.com' || host === 'calendar.google.com') &&
      url.pathname.includes('/calendar/event')
    );
  } catch {
    return false;
  }
};

const pushUnique = (found: string[], value: string) => {
  if (!found.includes(value)) found.push(value);
};

const collectCalendarLinks = (
  value: unknown,
  depth: number,
  found: string[]
) => {
  if (depth > LINK_WALK_DEPTH || found.length > 1) return;
  if (Array.isArray(value)) {
    for (const item of value) collectCalendarLinks(item, depth + 1, found);
    return;
  }

  const record = asRecord(value);
  if (!record) return;

  if (
    typeof record.htmlLink === 'string' &&
    isCalendarEventUrl(record.htmlLink)
  ) {
    pushUnique(found, record.htmlLink);
  }

  for (const nested of Object.values(record)) {
    if (nested !== null && typeof nested === 'object') {
      collectCalendarLinks(nested, depth + 1, found);
    }
  }
};

const isGmailMessage = (
  record: Record<string, unknown>
): record is Record<string, unknown> & { id: string } => {
  if (typeof record.id !== 'string' || !record.id.trim()) return false;
  return (
    typeof record.threadId === 'string' ||
    Array.isArray(record.labelIds) ||
    typeof record.snippet === 'string'
  );
};

const OUTLOOK_HOSTS = new Set([
  'outlook.live.com',
  'outlook.office.com',
  'outlook.office365.com',
  'outlook.cloud.microsoft',
]);

const isOutlookItemUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:') return false;
    if (!OUTLOOK_HOSTS.has(url.hostname)) return false;

    const params = new URLSearchParams(
      [...url.searchParams].map(([key, param]) => [key.toLowerCase(), param])
    );
    if (params.get('itemid')) return true;
    if (params.get('viewmodel') === 'readmessageitem') return true;
    return /\/(mail|calendar)\//i.test(url.pathname);
  } catch {
    return false;
  }
};

const collectOutlookLinks = (
  value: unknown,
  depth: number,
  found: string[]
) => {
  if (depth > LINK_WALK_DEPTH || found.length > 1) return;
  if (Array.isArray(value)) {
    for (const item of value) collectOutlookLinks(item, depth + 1, found);
    return;
  }

  const record = asRecord(value);
  if (!record) return;

  if (typeof record.webLink === 'string' && isOutlookItemUrl(record.webLink)) {
    pushUnique(found, record.webLink);
  }

  for (const nested of Object.values(record)) {
    if (nested !== null && typeof nested === 'object') {
      collectOutlookLinks(nested, depth + 1, found);
    }
  }
};

const collectGmailIds = (value: unknown, depth: number, found: string[]) => {
  if (depth > LINK_WALK_DEPTH || found.length > 1) return;
  if (Array.isArray(value)) {
    for (const item of value) collectGmailIds(item, depth + 1, found);
    return;
  }

  const record = asRecord(value);
  if (!record) return;

  if (isGmailMessage(record)) pushUnique(found, record.id.trim());

  for (const nested of Object.values(record)) {
    if (nested !== null && typeof nested === 'object') {
      collectGmailIds(nested, depth + 1, found);
    }
  }
};

type PipedriveKind = 'deal' | 'person' | 'organization' | 'lead';

const PIPEDRIVE_PATH: Record<PipedriveKind, string> = {
  deal: 'deal',
  person: 'person',
  organization: 'organization',
  lead: 'leads/inbox',
};

const pipedriveKindFromTool = (toolName: string): PipedriveKind | null => {
  const name = toolName.toLowerCase();
  if (!name.includes('pipedrive')) return null;
  if (name.includes('organi')) return 'organization';
  if (name.includes('person')) return 'person';
  if (name.includes('deal')) return 'deal';
  if (name.includes('lead')) return 'lead';
  return null;
};

const pipedriveId = (value: unknown): string | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'string' && value.trim()) return value.trim();
  return null;
};

const isPipedriveItemUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:') return false;
    if (!url.hostname.endsWith('.pipedrive.com')) return false;
    if (
      url.hostname === 'www.pipedrive.com' ||
      url.hostname === 'app.pipedrive.com'
    ) {
      return false;
    }
    return /\/(deal|person|organization|leads\/inbox)\/[^/]+$/i.test(
      url.pathname
    );
  } catch {
    return false;
  }
};

const explicitPipedriveUrl = (
  record: Record<string, unknown> | null
): string | null => {
  if (!record) return null;
  for (const key of ['webLink', 'htmlLink', 'url'] as const) {
    const value = textField(record[key]);
    if (value && isPipedriveItemUrl(value)) return value;
  }
  return null;
};

const companyDomainFrom = (value: unknown, depth = 0): string | null => {
  if (depth > LINK_WALK_DEPTH) return null;
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = companyDomainFrom(item, depth + 1);
      if (found) return found;
    }
    return null;
  }

  const record = asRecord(value);
  if (!record) return null;

  for (const key of ['company_domain', 'companyDomain'] as const) {
    const domain = textField(record[key])
      .replace(/^https?:\/\//, '')
      .replace(/\/.*$/, '')
      .replace(/\.pipedrive\.com$/i, '');
    if (/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i.test(domain)) {
      return domain;
    }
  }

  for (const nested of Object.values(record)) {
    if (nested !== null && typeof nested === 'object') {
      const found = companyDomainFrom(nested, depth + 1);
      if (found) return found;
    }
  }

  return null;
};

const pipedriveTargets = (
  parsed: unknown
): { ids: string[]; url: string | null } => {
  const record = asRecord(parsed);
  if (!record) return { ids: [], url: null };

  if (Array.isArray(record.records)) {
    const ids: string[] = [];
    let url: string | null = null;
    for (const hit of record.records) {
      const item = asRecord(asRecord(hit)?.item);
      const id = pipedriveId(item?.id);
      if (!id) continue;
      ids.push(id);
      url = url ?? explicitPipedriveUrl(item);
    }
    return { ids, url: ids.length === 1 ? url : null };
  }

  const data = asRecord(record.data);
  if (!data) return { ids: [], url: null };
  const id = pipedriveId(data.id);
  return {
    ids: id ? [id] : [],
    url: explicitPipedriveUrl(data),
  };
};

const readPipedriveLink = (
  toolName: string,
  parsed: unknown
): ExternalResourceLink | null => {
  const kind = pipedriveKindFromTool(toolName);
  if (!kind) return null;

  const { ids, url } = pipedriveTargets(parsed);
  if (ids.length !== 1) return null;
  if (url) {
    return { href: url, service: 'pipedrive', label: 'Open in Pipedrive' };
  }

  const domain = companyDomainFrom(parsed);
  const host = domain ? `${domain}.pipedrive.com` : 'app.pipedrive.com';

  return {
    href: `https://${host}/${PIPEDRIVE_PATH[kind]}/${encodeURIComponent(ids[0])}`,
    service: 'pipedrive',
    label: 'Open in Pipedrive',
  };
};

/**
 * One openable resource for a tool row.
 * Calendar uses the event's htmlLink. Outlook uses webLink. One message or
 * event gets a link, including a list that contains only that one item.
 * Pipedrive uses one record id. A company domain on the payload is the
 * account host. Otherwise the link uses app.pipedrive.com, which sends the
 * signed-in user to that record. Gmail has no link, so a single message id is
 * appended to the mailbox prefix. Several matches stay in the JSON.
 */
export function readExternalResourceLink(
  toolName: string,
  output: unknown
): ExternalResourceLink | null {
  const parsed = unwrapTruncatedPreview(parseToolOutput(output));
  const calendarLinks: string[] = [];
  collectCalendarLinks(parsed, 0, calendarLinks);
  if (calendarLinks.length === 1) {
    return {
      href: calendarLinks[0],
      service: 'calendar',
      label: 'Open in Calendar',
    };
  }
  if (calendarLinks.length > 1) return null;

  if (/outlook/i.test(toolName)) {
    const outlookLinks: string[] = [];
    collectOutlookLinks(parsed, 0, outlookLinks);
    if (outlookLinks.length === 1) {
      return {
        href: outlookLinks[0],
        service: 'outlook',
        label: 'Open in Outlook',
      };
    }
    if (outlookLinks.length > 1) return null;
  }

  if (/pipedrive/i.test(toolName)) {
    const pipedriveLink = readPipedriveLink(toolName, parsed);
    if (pipedriveLink) return pipedriveLink;
  }

  if (!/gmail/i.test(toolName)) return null;

  const messageIds: string[] = [];
  collectGmailIds(parsed, 0, messageIds);
  if (messageIds.length !== 1) return null;

  return {
    href: `${GMAIL_MESSAGE_PREFIX}${encodeURIComponent(messageIds[0])}`,
    service: 'gmail',
    label: 'Open in Gmail',
  };
}
